import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { toFullName } from '@repo/toolkit/to-full-name'
import ActionButton from '@repo/ui/action-button'
import Button from '@repo/ui/button'
import DataTable, { Column, DataTableRef } from '@repo/ui/data-table'
import { TableDate } from '@repo/ui/data-table-cells'
import { DeleteIcon, PenIcon, PlusIcon, RelationIcon } from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import { useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'

import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { genderEnumMapping, toGenderEnumValue } from '../../../lib/types/enums/common/gender.enum'
import { HostFamilyInput } from '../../../lib/types/models/host-family/host-family-input'
import { HostFamilyRelativeInput } from '../../../lib/types/models/host-family-relative/host-family-relative-input'
import { IdInput } from '../../../lib/types/utils'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import ActionTableWrapper from '../../elements/action-table-wrapper'
import HostFamilyRelativeForm from './host-family-relative-form'

const HostFamilyRelativeTableForm = () => {
  const [index, setIndex] = useState<number>()
  const genderMapping = genderEnumMapping()

  const form = useFormContext<HostFamilyInput>()

  const { modalState, selectedData, closeModel, toggleModal } = useModalManager<
    'create' | 'edit' | 'delete',
    HostFamilyRelativeInput
  >(['create', 'edit', 'delete'])
  const relatives = form.watch('relatives') || []

  const ref = useRef<DataTableRef>(null)

  const columns: Column<IdInput<HostFamilyRelativeInput>>[] = [
    {
      enableSorting: true,
      header: 'Last name',
      accessorKey: 'lastName'
    },
    {
      enableSorting: true,
      header: 'First name',
      accessorKey: 'firstName'
    },
    {
      enableSorting: true,
      header: 'Gender',
      accessorKey: 'gender',
      cell: ({ row }) => genderMapping[toGenderEnumValue(row.original.gender)]
    },
    {
      enableSorting: true,
      header: 'Date of birth',
      accessorKey: 'birthDate',
      cell: ({ row }) => <TableDate date={row.original.birthDate} />
    },
    {
      enableSorting: true,
      header: 'Relationship to the family',
      accessorKey: 'relationToFamily'
    },
    {
      enableSorting: false,
      header: '',
      exportable: false,
      accessorKey: 'id',
      cell: ({ row }) => (
        <div className={'flex gap-2'}>
          <ActionButton
            variant={'grey'}
            icon={PenIcon}
            onClick={() => {
              toggleModal('edit', row.original)
            }}
          />
          <ActionButton
            variant={'danger'}
            icon={DeleteIcon}
            onClick={() => {
              setIndex(row.index)
              toggleModal('delete')
            }}
          />
        </div>
      )
    }
  ]

  return (
    <ActionTableWrapper
      title={'Other people living in the house'}
      actions={[
        <Button
          key={'button'}
          type="button"
          icon={PlusIcon}
          label={'Add a person living at home'}
          variant={'outline'}
          onClick={() => toggleModal('create')}
        />
      ]}
    >
      {relatives.length > 0 && (
        <DataTable<IdInput<HostFamilyRelativeInput>>
          ref={ref}
          manualSorting={false}
          columns={columns}
          data={relatives.map((relative, index) => ({ ...relative, id: index }))}
          rowCount={relatives.length}
          paginated={false}
        />
      )}

      <Modal
        isOpen={modalState.create}
        title={'Add a person living at home'}
        icon={() => <RelationIcon color={tailwindColors.secondary.DEFAULT} />}
        size="medium"
        hideActions
        onCancel={() => closeModel('create')}
      >
        <HostFamilyRelativeForm
          className={MODAL_BODY_CLASSNAME}
          buttonsConfig={modalButtonConfig({
            isEnglish: true,
            onCancel: () => closeModel('create'),
            onConfirm: (value) => {
              form.setValue('relatives', [...relatives, value])
              closeModel('create')
            }
          })}
        />
      </Modal>

      <Modal
        isOpen={modalState.edit}
        title={toFullName(selectedData?.firstName, selectedData?.lastName)}
        icon={() => <RelationIcon color={tailwindColors.secondary.DEFAULT} />}
        size="medium"
        hideActions
        onCancel={() => closeModel('edit')}
      >
        <HostFamilyRelativeForm
          className={MODAL_BODY_CLASSNAME}
          defaultValues={selectedData}
          buttonsConfig={modalButtonConfig({
            onCancel: () => closeModel('edit'),
            onConfirm: (value) => {
              form.setValue(
                'relatives',
                relatives.map((relative) => (relative.id === value?.id ? value : relative))
              )
              closeModel('edit')
            }
          })}
        />
      </Modal>
      {index !== undefined && (
        <Modal
          actionButtonsOptions={{
            confirmText: 'Delete',
            cancelText: 'Cancel',
            onConfirm: () => {
              form.setValue(
                'relatives',
                relatives.filter((_, i) => i !== index)
              )
              closeModel('delete')
            }
          }}
          onCancel={() => toggleModal('delete')}
          isOpen={modalState.delete}
          title={`Are you sure you want to delete ${relatives[index]?.firstName} ${relatives[index]?.lastName}?`}
          icon={DeleteIcon}
          variant={'destructive'}
        />
      )}
    </ActionTableWrapper>
  )
}

export default HostFamilyRelativeTableForm
